/// <amd-module name="Dashboards/WidgetHost/HostImplementation/LegacyWidgetHostImplementation" />
import { WidgetPresentationMode, WidgetProps, WidgetSize } from "Dashboards/WidgetContracts/WidgetProps";
import * as Resources from "Dashboards/WidgetHost/Resources";
import * as React from "react";
import { Lightbox } from "Reporting/Lightbox/Lightbox";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { getLegacyLightboxGridSize } from "../Helpers/presentationContextHelpers";
import { WidgetHostImplementationBase, WidgetHostImplementationProps } from "./WidgetHostImplementationBase";

export interface LegacyWidgetHostProps extends WidgetHostImplementationProps {}

/**
 * Deals with the secret of interop invocation to legacy.
 *
 * Legacy-side Adapter implementation converts the WidgetHostProps data and callbacks into format appropriate to Legacy.
 *
 * What differs:
 * -Widget Rendering
 * -Grid-centric Lightbox sizing
 */
export class LegacyWidgetHostImplementation extends WidgetHostImplementationBase<LegacyWidgetHostProps> {
    protected getHostType(): string {
        return "LegacyWidgetHost";
    }

    protected renderWidgetHostImplementation(widgetProps: WidgetProps): JSX.Element {
        return <LegacyWidgetHostRenderer key={widgetProps.id} {...widgetProps} />;
    }

    protected getRenderSize(): WidgetSize {
        return this.props.widgetPresentationMode === WidgetPresentationMode.lightboxView
            ? this.props.convertToPixelSize(getLegacyLightboxGridSize())
            : this.props.convertToPixelSize(this.props.widgetProps.size);
    }

    /** Renders a modern widget in the lightbox. */
    protected onShowLightbox = (): void => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const lighboxProps = { ...this.props.widgetProps };
            lighboxProps.size = getLegacyLightboxGridSize();
            const lightboxSizeStyle = this.props.convertToPixelSize(lighboxProps.size);

            return (
                <Lightbox onDismiss={onDismiss} title={Resources.LightboxTitle}>
                    <div style={lightboxSizeStyle}>
                        <div className="widget-host render-in-lightbox" style={lightboxSizeStyle}>
                            <LegacyWidgetHostImplementation
                                {...this.props}
                                widgetProps={lighboxProps}
                                widgetPresentationMode={WidgetPresentationMode.lightboxView}
                            />
                        </div>
                    </div>
                </Lightbox>
            );
        });
    };
}

/** Provides an intermediate layer for preventing re-renders of children */
class LegacyWidgetHostRenderer extends React.PureComponent<WidgetProps> {
    public render(): JSX.Element {
        return (
            <LegacyComponent
                key={this.props.id}
                modules={["Dashboards/Components/LegacyWidgetHostAdapter"]}
                wrappedType="LegacyWidgetHostAdapter"
                {...this.props}
            />
        );
    }
}
